import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../api/client';

const LANGS = [
  { code: 'ru', label: 'РУС' },
  { code: 'ky', label: 'КЫР' },
  { code: 'en', label: 'ENG' },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const [titles, setTitles] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [socialLinks, setSocialLinks] = useState<{ instagram: string; facebook: string; telegram: string }>({ instagram: '', facebook: '', telegram: '' });
  const dropdownRef = useRef<HTMLLIElement>(null);
  const location = useLocation();

  const NAV_LINKS = [
    { label: t('nav_home'),      href: '/' },
    { label: t('nav_join'),      href: '/join' },
    { label: t('nav_documents'), href: '/documents' },
    { label: t('nav_news'),      href: '/news' },
    { label: t('nav_faq'),       href: '/faq' },
    { label: t('nav_contacts'),  href: '/contacts' },
  ];

  useEffect(() => {
    axios.get(`${API_BASE}/activities/get-all-titles`)
      .then(res => setTitles(res.data))
      .catch(() => {});
  }, [i18n.language]);

  useEffect(() => {
    axios.get(`${API_BASE}/social-links`)
      .then(res => {
        if (res.data.length > 0) {
          setSocialLinks(res.data[0]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
        setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const closeAll = () => { setMobileOpen(false); setDropdownOpen(false); };

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem('lang', code);
  };

  return (
    <>
      {/* Top bar */}
      <div className="header-topbar">
        <div className="header-topbar-inner">
          <span style={{ fontSize: '0.82rem', opacity: 0.85 }}>
            {t('header_org')}
          </span>
          <div className="header-topbar-socials">
            {socialLinks.instagram && <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer">Instagram</a>}
            {socialLinks.facebook && <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer">Facebook</a>}
            {socialLinks.telegram && <a href={socialLinks.telegram} target="_blank" rel="noopener noreferrer">Telegram</a>}
            <span style={{ margin: '0 4px', opacity: 0.4 }}>|</span>
            {LANGS.map((lang, i) => (
              <React.Fragment key={lang.code}>
                <button
                  onClick={() => changeLang(lang.code)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: '0.78rem', fontWeight: 700, padding: '2px 4px',
                    color: i18n.language === lang.code ? '#fff' : 'rgba(255,255,255,0.6)',
                    borderBottom: i18n.language === lang.code ? '2px solid #fff' : '2px solid transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  {lang.label}
                </button>
                {i < LANGS.length - 1 && <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>|</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className="header">
        <Link to="/" className="header-logo" onClick={closeAll} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <img src="/logo.png" alt="logo" style={{ height: 52, width: 'auto', display: 'block' }} />
          <span>{t('header_org')}</span>
        </Link>

        <nav className="header-nav-desktop">
          <ul className="header-nav">
            {NAV_LINKS.map(link => (
              <li key={link.href}>
                <Link
                  to={link.href}
                  style={{ color: location.pathname === link.href ? 'var(--primary)' : undefined }}
                >
                  {link.label}
                </Link>
              </li>
            ))}

            <li ref={dropdownRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setDropdownOpen(p => !p)}
                style={{ color: dropdownOpen ? 'var(--primary)' : undefined, display: 'flex', alignItems: 'center', gap: 4 }}
              >
                {t('nav_activities')}
                <span style={{ fontSize: '0.65rem', transition: 'transform 0.2s', display: 'inline-block', transform: dropdownOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
              </button>

              {dropdownOpen && (
                <ul style={{
                  position: 'absolute', top: 'calc(100% + 14px)', right: 0,
                  background: '#fff', borderRadius: 12,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  minWidth: 'clamp(180px, 40vw, 240px)', listStyle: 'none', padding: '8px 0', zIndex: 200,
                  border: '1px solid rgba(0,0,0,0.06)',
                }}>
                  {titles.length === 0 ? (
                    <li style={{ padding: '10px 18px', color: '#9ca3af', fontSize: '0.88rem' }}>{t('header_loading')}</li>
                  ) : titles.map((title, i) => (
                    <li key={i}>
                      <Link
                        to={`/activities?title=${encodeURIComponent(title)}`}
                        onClick={() => setDropdownOpen(false)}
                        style={{ display: 'block', padding: '10px 18px', color: '#374151', textDecoration: 'none', fontSize: '0.88rem', fontWeight: 500 }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        {title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          </ul>
        </nav>

        <button className="burger-btn" onClick={() => setMobileOpen(true)}>
          <span /><span /><span />
        </button>
      </header>

      {mobileOpen && (
        <div className="mobile-menu">
          <button className="mobile-menu-close" onClick={closeAll}>✕</button>
          <nav className="mobile-menu-nav">
            {NAV_LINKS.map(link => (
              <Link key={link.href} to={link.href} className="mobile-menu-link" onClick={closeAll}>
                {link.label}
              </Link>
            ))}
            <div className="mobile-menu-divider" />
            <div className="mobile-menu-label">{t('nav_activities')}</div>
            {titles.map((title, i) => (
              <Link
                key={i}
                to={`/activities?title=${encodeURIComponent(title)}`}
                className="mobile-menu-link mobile-menu-link-sub"
                onClick={closeAll}
              >
                {title}
              </Link>
            ))}
            <div className="mobile-menu-divider" />
            <div className="mobile-menu-divider" />
            {/* Переключатель языка в мобильном меню */}
            <div style={{ display: 'flex', gap: 12, padding: '12px 0' }}>
              {LANGS.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => { changeLang(lang.code); closeAll(); }}
                  style={{
                    background: i18n.language === lang.code ? '#1a3a6b' : 'transparent',
                    color: i18n.language === lang.code ? '#fff' : '#1a3a6b',
                    border: '2px solid #1a3a6b', borderRadius: 6,
                    padding: '6px 14px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                  }}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
